'use client';

import { createContext, useContext } from 'react';
import { getPublishedPosts, type BlogPostDocument } from '../../firebase/blog-db';
import { extractText } from '../../lib/readingTime';

/**
 * Abstraction over blog search so a real search backend (Algolia, Typesense,
 * a future Cloud Function) can be swapped in later without touching
 * BlogSearchPage. The interim implementation below is plain client-side
 * substring matching over a capped batch of published posts — NOT full-text
 * search (no stemming, ranking, or typo tolerance), matching how search
 * already works elsewhere in this app (MarketplacePage).
 */
export interface BlogSearchProvider {
  search(term: string): Promise<BlogPostDocument[]>;
}

const SEARCH_CAP = 200;

export class ClientSubstringSearchProvider implements BlogSearchProvider {
  async search(term: string): Promise<BlogPostDocument[]> {
    const s = term.trim().toLowerCase();
    if (!s) return [];
    const { posts } = await getPublishedPosts(SEARCH_CAP);
    // Strip a leading '#' — hashtags in article bodies link here, but the
    // body text itself never includes the '#'.
    const bodyTerm = s.startsWith('#') ? s.slice(1) : s;
    return posts.filter(p =>
      p.title.toLowerCase().includes(s) ||
      p.excerpt.toLowerCase().includes(s) ||
      p.categoryName.toLowerCase().includes(s) ||
      p.tagNames.some(t => t.toLowerCase().includes(s)) ||
      extractText(p.contentJson).toLowerCase().includes(bodyTerm)
    );
  }
}

const defaultProvider = new ClientSubstringSearchProvider();
const BlogSearchContext = createContext<BlogSearchProvider>(defaultProvider);

export const useBlogSearch = () => useContext(BlogSearchContext);
export const BlogSearchProviderContext = BlogSearchContext.Provider;
